import { computed, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { UiState } from '@/api/copilot';

/**
 * Compact view of where the user currently is in the SPA. Sent to the
 * Copilot on every turn so it can resolve "this page", "current scenario",
 * "this call" without asking.
 *
 * Per ADR-0044 (philosophy §1.5): we also send a business-language
 * `screen` label so the assistant can talk about "you're on the
 * scenario editor" instead of `route=scenario_edit`. The route stays
 * as a stable identifier for cross-screen reasoning; `screen` is
 * presentation-friendly.
 */
export function useUiState(): ComputedRef<UiState> {
  const route = useRoute();
  const { t, te } = useI18n();
  return computed(() => {
    const out: UiState = {};
    const name = typeof route.name === 'string' ? route.name : undefined;
    if (name) {
      const stable = mapRoute(name, String(route.hash ?? ''));
      out.route = stable;
      // Look up a localised business label via i18n; fall back silently
      // when a route hasn't been labelled yet (e.g. brand-new admin
      // pages). The assistant will read just `route` in that case.
      const key = `screens.${stable}`;
      if (te(key)) out.screen = t(key);
    }
    const tab = tabFromHash(String(route.hash ?? ''));
    if (tab) out.activeTab = tab;

    const scenarioId = route.params.scenarioId;
    if (typeof scenarioId === 'string' && scenarioId.length > 0 && isUuid(scenarioId)) {
      out.scenarioId = scenarioId;
    }
    const sessionId = route.params.sessionId;
    if (typeof sessionId === 'string' && sessionId.length > 0 && isUuid(sessionId)) {
      out.sessionId = sessionId;
    }
    const projectId = route.params.projectId;
    if (typeof projectId === 'string' && projectId.length > 0 && isUuid(projectId)) {
      out.projectId = projectId;
    }
    // BCP-47 browser locale (e.g. "en-US", "ru-RU"). Sent on every turn
    // so the copilot can pick a reply language when the user hasn't
    // written anything yet and `project.defaultLanguage` is not set —
    // typical on projects created before the locale plumbing landed.
    const navLocale = readBrowserLocale();
    if (navLocale) out.clientLocale = navLocale;
    return out;
  });
}

function readBrowserLocale(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const primary = navigator.language;
  if (typeof primary === 'string' && primary.length > 0) return primary;
  const list = navigator.languages;
  if (Array.isArray(list) && list.length > 0 && typeof list[0] === 'string') {
    return list[0];
  }
  return undefined;
}

function mapRoute(name: string, _hash: string): string {
  // Keep route names stable in the wire so the model can reason about them.
  // Per ADR-0044: every value here MUST have a matching `screens.<key>`
  // entry in i18n/locales/ru.json so the assistant can pronounce it back
  // to the user in business language.
  switch (name) {
    case 'dashboard':
      return 'dashboard';
    case 'quickstart':
      return 'quickstart';
    case 'project-detail':
      return 'project_detail';
    case 'project-edit':
      return 'project_edit';
    case 'project-new':
      return 'project_new';
    case 'scenario-edit':
      return 'scenario_edit';
    case 'scenario-new':
    case 'scenario-new-form':
      return 'scenario_new';
    case 'goal-new':
    case 'goal-new-form':
      return 'goal_new';
    case 'scenario-prompt-edit':
      return 'scenario_prompt_edit';
    case 'script-edit':
      return 'script_edit';
    case 'step-prompt-edit':
      return 'step_prompt_edit';
    case 'session-detail':
      return 'session_detail';
    case 'calls':
      return 'calls';
    case 'reports-telephony':
      return 'reports_telephony';
    case 'industry-packs':
      return 'industry_packs';
    case 'industry-pack-detail':
      return 'industry_pack_detail';
    case 'webhooks-aggregator':
      return 'webhooks_aggregator';
    case 'api-keys-aggregator':
      return 'api_keys_aggregator';
    case 'onboarding-entry':
      return 'onboarding_entry';
    case 'onboarding-goals':
      return 'onboarding_goals';
    case 'admin-economics':
      return 'admin_economics';
    case 'admin-industry-packs':
      return 'admin_industry_packs';
    case 'login':
      return 'login';
    default:
      return name;
  }
}

function tabFromHash(hash: string): string | undefined {
  if (!hash) return undefined;
  const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  return stripped || undefined;
}

function isUuid(v: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}
